import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';
import UserSidebar from '../../partials/users/UserSidebar';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import PasswordResetSidebar from '../../partials/users/PasswordResetSidebar';
import InviteCreateSidebar from '../../partials/users/InviteCreateSidebar';

function Users() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userSidebarOpen, setUserSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState(null);
  const [passwordResetSidebarOpen, setPasswordResetSidebarOpen] = useState(false);
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState(null);
  const [inviteSidebarOpen, setInviteSidebarOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Config.APIBaseURL}/users/`, {
        headers: {
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });
      setUsers(response.data);
      setIsLoading(false);
    } catch (error) {
      setError('Error fetching users');
      setIsLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserSidebarOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserSidebarOpen(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUserForPasswordReset(user);
    setPasswordResetSidebarOpen(true);
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Sidebar */}
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        {/*  Site header */}
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        {/* Main content */}
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-8">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">User Management</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Manage users, roles, and access.</p>
            </div>
            <div className="flex space-x-2">
              <button
                className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                onClick={() => setInviteSidebarOpen(true)}
              >
                <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                  <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                </svg>
                <span className="hidden xs:block ml-2">Create Invite</span>
              </button>
              <button
                className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                onClick={handleCreateUser}
              >
                <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
                  <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                </svg>
                <span className="hidden xs:block ml-2">Create User</span>
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
              <div className="overflow-x-auto">
                <table className="table-auto w-full">
                  <thead className="text-xs font-semibold uppercase text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-700/20">
                    <tr>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left"></div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Name</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Username</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Email</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Organization</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Role</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Status</div>
                      </th>
                      <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-semibold text-left">Actions</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="w-10 h-10 shrink-0 flex items-center justify-center">
                            {user.user_info?.avatar ? (
                              <img 
                                className="rounded-full" 
                                src={user.user_info.avatar.startsWith('data:') 
                                  ? user.user_info.avatar 
                                  : `data:image/jpeg;base64,${user.user_info.avatar}`
                                }
                                width="40" 
                                height="40" 
                                alt="User Avatar" 
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 font-semibold">
                                {user.username?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left font-medium text-slate-800 dark:text-slate-100">
                            {user.user_info?.first_name && user.user_info?.last_name 
                              ? `${user.user_info.first_name} ${user.user_info.last_name}`
                              : 'N/A'
                            }
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left font-medium text-slate-800 dark:text-slate-100">{user.username}</div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">{user.email}</div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">{user.user_info?.organization?.short_name || 'N/A'}</div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">{user.role}</div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 ${
                            user.is_active ? 'bg-emerald-100 dark:bg-emerald-400/30 text-emerald-600 dark:text-emerald-400' 
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                              onClick={() => handleEditUser(user)}
                            >
                              <span className="sr-only">Edit</span>
                              <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
                              </svg>
                            </button>
                            <button
                              className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                              onClick={() => handleResetPassword(user)}
                            >
                              <span className="sr-only">Reset Password</span>
                              <svg 
                                className="svg-icon fill-current" 
                                style={{width: '1.7em', height: '1.7em', verticalAlign: 'middle', overflow: 'hidden'}}
                                viewBox="0 0 1024 1024" 
                                version="1.1" 
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M288 416v-96a128 128 0 0 1 256 0v96h64v-96c0-106-86-192-192-192s-192 86-192 192v96zM512 704h-64v-64l384-384 64 64-384 384z" />
                                <path d="M544 736H416V608l160-160H192a64.19 64.19 0 0 0-64 64v320a64.19 64.19 0 0 0 64 64h448a64.19 64.19 0 0 0 64-64V576z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <UserSidebar
            userSidebarOpen={userSidebarOpen}
            setUserSidebarOpen={setUserSidebarOpen}
            selectedUser={selectedUser}
            onUserAdded={fetchUsers}
          />
          <PasswordResetSidebar
            sidebarOpen={passwordResetSidebarOpen}
            setSidebarOpen={setPasswordResetSidebarOpen}
            selectedUser={selectedUserForPasswordReset}
          />
          <InviteCreateSidebar
            sidebarOpen={inviteSidebarOpen}
            setSidebarOpen={setInviteSidebarOpen}
          />
        </div>
      </div>
    </div>
  );
}

export default Users; 